import { useEffect, useRef, useState } from 'react';
import {
  createBackoffRetry,
  epochKeyRing,
  ErrorCodes,
  SocketEvents,
  syncChannelEpochKey,
} from '@clinikchat/shared';
import { useCryptoStore } from '../stores/cryptoStore';
import api from '../lib/api';
import { getSocket, useSocketStore } from '../lib/socket';

export const EPOCH_VERIFY_RETRY_BASE_MS = 2000;
export const EPOCH_VERIFY_RETRY_MAX_MS = 30_000;
export const EPOCH_VERIFY_RETRY_MAX_ATTEMPTS = 6;

interface UseChannelEpochKeyResult {
  key: Uint8Array | null;
  error: string | null;
}

/**
 * Keeps this device on a channel's current key epoch (see syncChannelEpochKey):
 * on mount, whenever the server announces a rotation or refuses a write under a
 * superseded epoch, and by bounded backoff after a transient failure.
 *
 * If `epoch` is given only that epoch's key is fetched. Keys live in the
 * in-memory epoch key ring and are never persisted.
 */
export function useChannelEpochKey(channelId: string, epoch?: number): UseChannelEpochKeyResult {
  const secretKey = useCryptoStore((s) => s.secretKey);
  const socketId = useSocketStore((s) => s.socketId);
  const [result, setResult] = useState<UseChannelEpochKeyResult>({ key: null, error: null });
  // Bumped by rotation announcements and refusals; re-runs the sync.
  const [signal, setSignal] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const retryRef = useRef(
    createBackoffRetry(
      {
        baseMs: EPOCH_VERIFY_RETRY_BASE_MS,
        maxMs: EPOCH_VERIFY_RETRY_MAX_MS,
        maxAttempts: EPOCH_VERIFY_RETRY_MAX_ATTEMPTS,
      },
      () => setRetryTick((t) => t + 1),
    ),
  );
  useEffect(() => () => retryRef.current.cancel(), []);

  useEffect(() => {
    if (!channelId) return;
    const socket = getSocket();
    if (!socket) return;
    const onRotation = (payload: { channelId: string; nextEpoch?: number; epoch?: number }) => {
      if (payload?.channelId !== channelId) return;
      // Both events name the epoch sends must wait for. Record it NOW, before the
      // asynchronous sync: until it succeeds (possibly after a whole outage) the
      // ring's latest key is superseded and a removed member still holds it.
      const announced = payload.nextEpoch ?? payload.epoch;
      if (typeof announced === 'number') epochKeyRing.requireEpoch(channelId, announced);
      setSignal((v) => v + 1);
    };
    // The server refused a write under a superseded epoch: this device is behind a
    // rotation it never heard about. Same recovery as an announcement.
    const onSocketError = (payload: { code?: string; channelId?: string }) => {
      if (payload?.code !== ErrorCodes.KEY_EPOCH_SUPERSEDED || payload.channelId !== channelId) {
        return;
      }
      setSignal((v) => v + 1);
    };
    socket.on(SocketEvents.CHANNEL_KEY_ROTATION_NEEDED, onRotation);
    socket.on(SocketEvents.CHANNEL_KEY_ROTATED, onRotation);
    socket.on(SocketEvents.ERROR, onSocketError);
    return () => {
      socket.off(SocketEvents.CHANNEL_KEY_ROTATION_NEEDED, onRotation);
      socket.off(SocketEvents.CHANNEL_KEY_ROTATED, onRotation);
      socket.off(SocketEvents.ERROR, onSocketError);
    };
  }, [channelId, socketId]);

  useEffect(() => {
    if (!channelId || !secretKey) return;
    const retryKey = `${channelId}:${socketId}`;
    // A slower earlier pass must not overwrite a newer one's result.
    let superseded = false;
    void (async () => {
      const outcome = await syncChannelEpochKey(
        {
          api,
          secretKey,
          onKeyDistributed: (id, keyB64) => useCryptoStore.getState().setChannelKey(id, keyB64),
          legacyFlatKey: (id) => useCryptoStore.getState().channelKeys[id],
        },
        channelId,
        { epoch, bootstrapFirstEpoch: true },
      );
      if (superseded || outcome.outcome === 'dropped') return;
      if (outcome.retry) retryRef.current.schedule(retryKey);
      else if (outcome.confirmed && outcome.outcome === 'ready') retryRef.current.reset(retryKey);
      setResult({ key: outcome.key ?? null, error: outcome.error ?? null });
    })();
    return () => {
      superseded = true;
    };
  }, [channelId, epoch, secretKey, signal, socketId, retryTick]);

  return result;
}
